import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine, TrendingUp } from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import { SERVICES_CHART_CARD_ICON_COLOR } from "./constants";
import type { DiscountImpactPoint } from "./serviceDetailTypes";

const REVENUE_AXIS_ID = "revenue";
const DISCOUNT_AXIS_ID = "discount";
const REVENUE_COLOR = "#A78BFA";
const DISCOUNT_COLOR = "#737373";

interface ServiceDiscountImpactChartProps {
  data: DiscountImpactPoint[];
  savedPercent: number;
  totalProfit: number;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function formatCurrency(value: number): string {
  return formatAnalyticsCurrency(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

export const ServiceDiscountImpactChart: React.FC<
  ServiceDiscountImpactChartProps
> = ({ data, savedPercent, totalProfit, onExport, exportDisabled }) => {
  const { t } = useTranslation();
  const chartInstanceId = useAnalyticsChartInstanceId();
  const revenueLabel = t("analytics.overview.revenueSeries", "Revenue");
  const discountLabel = t("analytics.services.detail.discount", "Discount");
  const chartConfig = useMemo(
    () =>
      ({
        [REVENUE_AXIS_ID]: {
          label: revenueLabel,
          color: REVENUE_COLOR,
        },
        [DISCOUNT_AXIS_ID]: {
          label: discountLabel,
          color: DISCOUNT_COLOR,
        },
      }) satisfies ChartConfig,
    [discountLabel, revenueLabel],
  );
  const legendItems = useMemo(
    () => [
      { key: REVENUE_AXIS_ID, label: revenueLabel, color: REVENUE_COLOR },
      { key: DISCOUNT_AXIS_ID, label: discountLabel, color: DISCOUNT_COLOR },
    ],
    [discountLabel, revenueLabel],
  );

  return (
    <RevenueChartCard
      title={t(
        "analytics.services.detail.discountImpact",
        "Impact of Discount",
      )}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <ChartContainer
        config={chartConfig}
        className="!aspect-auto h-[280px] w-full"
      >
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 48, left: 0, bottom: 0 }}
        >
          <defs>
            <AnalyticsAreaFillDefs
              seriesKeys={[REVENUE_AXIS_ID]}
              chartInstanceId={chartInstanceId}
            />
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            yAxisId={REVENUE_AXIS_ID}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={48}
            tickFormatter={(value) => formatCurrency(Number(value))}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            yAxisId={DISCOUNT_AXIS_ID}
            orientation="right"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={40}
            tickFormatter={(value) => formatCurrency(Number(value))}
            className="text-muted-foreground text-xs"
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                indicator="dot"
                formatter={(value, name) => [
                  formatCurrency(Number(value)),
                  name === "revenue" ? revenueLabel : discountLabel,
                ]}
              />
            }
          />
          <Area
            yAxisId={REVENUE_AXIS_ID}
            type="monotone"
            dataKey={REVENUE_AXIS_ID}
            stroke="var(--color-revenue)"
            strokeWidth={2}
            fill={analyticsAreaFillUrl(REVENUE_AXIS_ID, chartInstanceId)}
          />
          <Line
            yAxisId={DISCOUNT_AXIS_ID}
            type="monotone"
            dataKey={DISCOUNT_AXIS_ID}
            stroke="var(--color-discount)"
            strokeWidth={2.5}
            dot={{
              r: 3,
              stroke: "var(--color-discount)",
              strokeWidth: 2,
              fill: "white",
            }}
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ChartContainer>
      <RevenueChartLegend items={legendItems} />
      <div className="space-y-0.5 font-sans">
        <div className="flex items-center gap-1.5 text-sm leading-none font-medium text-green-600">
          <span>
            {t(
              "analytics.services.detail.savedThisYear",
              "Had been saved {{percent}}% this Year",
              { percent: savedPercent },
            )}
          </span>
          <TrendingUp
            className="size-3.5 shrink-0"
            strokeWidth={2}
            aria-hidden
          />
        </div>
        <p className="text-xs leading-normal text-[#737373]">
          {t(
            "analytics.services.detail.totalProfit",
            "Total Profit: {{amount}}",
            {
              amount: formatCurrency(totalProfit),
            },
          )}
        </p>
      </div>
    </RevenueChartCard>
  );
};
